import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    FeedInlineState,
    FeedModalList,
    FeedReactionEmoji,
    FeedReactionFilter,
    FeedReactionFilters,
    FeedReactionName,
    FeedReactionRow,
    FeedRetryButton
} from "src/components/NoticeboardFeedUi";
import { StandardModal } from "src/components";
import { getNoticeReactions } from "../../api/feedApi";
import { getDisplayName } from "../../feedUtils";
import NoticeboardFeedAvatar from "./NoticeboardFeedAvatar";

const ALL_REACTIONS = "all";

const NoticeboardReactionsModal = ({ open, onClose, token, interactionUuid }) => {
    const [reactions, setReactions] = useState(null);
    const [filter, setFilter] = useState(ALL_REACTIONS);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const loadReactions = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            setReactions(await getNoticeReactions(token, interactionUuid));
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [interactionUuid, token]);

    useEffect(() => {
        if (open) {
            setFilter(ALL_REACTIONS);
            loadReactions();
        }
    }, [loadReactions, open]);

    const visibleUsers = useMemo(() => {
        if (!reactions) {
            return [];
        }
        if (filter === ALL_REACTIONS) {
            return reactions.userDetails;
        }

        return reactions.userDetails.filter(user => user.unicode === filter);
    }, [filter, reactions]);

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            title="Post reactions"
            description={reactions ? `${reactions.total} total ${reactions.total === 1 ? "reaction" : "reactions"}` : "See who reacted to this post."}
            widthMode="medium"
            disableEnforceFocus={false}
            disableScrollLock={false}
        >
            {loading && <FeedInlineState role="status">Loading reactions…</FeedInlineState>}
            {error && (
                <FeedInlineState $error role="alert">
                    Reactions could not be loaded.
                    <div><FeedRetryButton type="button" onClick={loadReactions}>Retry</FeedRetryButton></div>
                </FeedInlineState>
            )}
            {!loading && !error && reactions && (
                <>
                    <FeedReactionFilters aria-label="Filter reactions">
                        <FeedReactionFilter
                            type="button"
                            $active={filter === ALL_REACTIONS}
                            onClick={() => setFilter(ALL_REACTIONS)}
                        >
                            All {reactions.total}
                        </FeedReactionFilter>
                        {reactions.emojis.map(reaction => (
                            <FeedReactionFilter
                                key={reaction.unicode}
                                type="button"
                                $active={filter === reaction.unicode}
                                onClick={() => setFilter(reaction.unicode)}
                                aria-label={`Show ${reaction.count} ${reaction.unicode} reactions`}
                            >
                                {reaction.unicode} {reaction.count}
                            </FeedReactionFilter>
                        ))}
                    </FeedReactionFilters>
                    {!visibleUsers.length ? (
                        <FeedInlineState>No reactions to display.</FeedInlineState>
                    ) : (
                        <FeedModalList>
                            {visibleUsers.map(user => {
                                const displayName = getDisplayName(
                                    user.firstName,
                                    user.lastName,
                                    user.employeeName
                                );
                                return (
                                    <FeedReactionRow key={user.uuid}>
                                        <NoticeboardFeedAvatar
                                            displayName={displayName}
                                            profilePictureUrl={user.profilePictureUrl}
                                            size="2.5rem"
                                            fontSize="0.72rem"
                                        />
                                        <FeedReactionName>{displayName}</FeedReactionName>
                                        <FeedReactionEmoji aria-label={`Reacted ${user.unicode}`}>
                                            {user.unicode}
                                        </FeedReactionEmoji>
                                    </FeedReactionRow>
                                );
                            })}
                        </FeedModalList>
                    )}
                </>
            )}
        </StandardModal>
    );
};

export default NoticeboardReactionsModal;
